import { z } from 'zod';
/**
 * These identifiers are the stable Client Trial navigation contract.  Routes
 * may be registered before their owning gate is implemented, but only an
 * implemented route may be exposed by the web shell.
 */
export const ClientTrialRouteIdSchema = z.enum([
    'DASHBOARD',
    'CUSTOMERS',
    'LEADS',
    'TASKS',
    'CORPORATE',
    'INSURANCE',
    'CATALOG',
    'LOYALTY',
    'COMMUNICATIONS',
    'JOURNEYS',
    'AUTOMATION',
    'ANALYTICS',
    'BRANCHES_TEAM',
    'INTEGRATIONS',
    'AUDIT_OPERATIONS',
    'SETTINGS',
]);
export const ClientTrialRouteSchema = z.object({
    id: ClientTrialRouteIdSchema,
    path: z.string().startsWith('/'),
    label: z.string().min(1).max(80),
    audience: z.array(z.enum(['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER', 'MEMBER'])).min(1),
}).strict();
export const ClientTrialRouteContract = [
    { id: 'DASHBOARD', path: '/', label: 'Dashboard', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'CUSTOMERS', path: '/customers', label: 'Customers', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER', 'MEMBER'] },
    { id: 'LEADS', path: '/leads', label: 'Leads', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER', 'MEMBER'] },
    { id: 'TASKS', path: '/tasks', label: 'Tasks / Follow-ups', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER', 'MEMBER'] },
    { id: 'CORPORATE', path: '/corporate/companies', label: 'Corporate', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'INSURANCE', path: '/insurance/providers', label: 'Insurance', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'CATALOG', path: '/catalog/imports', label: 'Catalog', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'LOYALTY', path: '/loyalty', label: 'Loyalty', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'COMMUNICATIONS', path: '/communications', label: 'Communications', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'JOURNEYS', path: '/journeys', label: 'Customer Journeys', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'AUTOMATION', path: '/automation', label: 'Automation', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'ANALYTICS', path: '/analytics', label: 'Analytics', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'BRANCHES_TEAM', path: '/branches-team', label: 'Branches & Team', audience: ['OWNER', 'TENANT_ADMIN', 'BRANCH_MANAGER'] },
    { id: 'INTEGRATIONS', path: '/integrations', label: 'Integrations', audience: ['OWNER', 'TENANT_ADMIN'] },
    { id: 'AUDIT_OPERATIONS', path: '/audit-operations', label: 'Audit / Operations', audience: ['OWNER', 'TENANT_ADMIN'] },
    { id: 'SETTINGS', path: '/settings', label: 'Settings', audience: ['OWNER', 'TENANT_ADMIN'] },
];
export const ReportingPeriodSchema = z.enum(['TODAY', 'YESTERDAY', 'LAST_7_DAYS', 'LAST_30_DAYS']);
export const DashboardQuerySchema = z.object({
    organizationId: z.string().uuid(),
    period: ReportingPeriodSchema.default('TODAY'),
}).strict();
export const DashboardAttentionQuerySchema = z.object({
    organizationId: z.string().uuid(),
}).strict();
export const ProviderSourceKindSchema = z.enum([
    'WEBSITE', 'WOOCOMMERCE', 'BCONNECT', 'WHATSAPP', 'INSTAGRAM', 'FACEBOOK',
]);
export const ProviderSourceModeSchema = z.enum(['SYNTHETIC_TRIAL', 'LIVE']);
const ProviderEventEnvelopeBaseSchema = z.object({
    sourceKind: ProviderSourceKindSchema,
    sourceEventId: z.string().trim().min(1).max(200),
    sourceOccurredAt: z.string().datetime(),
    receivedAt: z.string().datetime(),
    organizationId: z.string().uuid(),
    branchId: z.string().uuid().nullable(),
    eventType: z.string().trim().regex(/^[a-z][a-z0-9_.]{2,127}$/),
    payload: z.record(z.string(), z.unknown()),
    externalIdentifiers: z.record(z.string(), z.string().trim().min(1).max(300)),
    correlationId: z.string().trim().min(1).max(128),
    idempotencyKey: z.string().trim().min(1).max(200),
}).strict();
export const ProviderEventEnvelopeSchema = z.discriminatedUnion('sourceMode', [
    ProviderEventEnvelopeBaseSchema.extend({
        sourceMode: z.literal('SYNTHETIC_TRIAL'),
        datasetId: z.string().uuid(),
    }).strict(),
    ProviderEventEnvelopeBaseSchema.extend({
        sourceMode: z.literal('LIVE'),
        datasetId: z.null(),
    }).strict(),
]);
export const AnalyticsEventVocabulary = [
    'session_started', 'page_viewed', 'category_viewed', 'product_viewed',
    'search_performed', 'add_to_cart', 'remove_from_cart', 'cart_viewed',
    'checkout_started', 'checkout_progressed', 'order_created', 'order_completed',
    'order_failed', 'order_cancelled', 'refund_created',
];
/**
 * Future Client Trial lanes own their event catalogues, but must use one
 * internal, namespaced shape so timeline, audit, and analytics integrations
 * can distinguish domains without inventing unreviewed prefixes.
 */
export const OperationalEventDomainSchema = z.enum([
    'crm', 'corporate', 'insurance', 'catalog', 'loyalty', 'automation', 'communications',
]);
export const OperationalEventNameSchema = z.string()
    .regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/)
    .superRefine((value, context) => {
    if (!OperationalEventDomainSchema.options.includes(value.split('.')[0])) {
        context.addIssue({ code: z.ZodIssueCode.custom, message: 'operational event domain is not approved' });
    }
});
export const isOperationalEventName = (value) => OperationalEventNameSchema.safeParse(value).success;
const DashboardStatusCountSchema = z.object({ status: z.string().min(1), count: z.number().int().nonnegative() }).strict();
const DashboardDimensionCountSchema = z.object({ id: z.string().uuid(), label: z.string().min(1), count: z.number().int().nonnegative() }).strict();
const DashboardWorkloadSchema = z.object({ membershipId: z.string().uuid(), displayName: z.string().nullable(), openTasks: z.number().int().nonnegative(), overdueTasks: z.number().int().nonnegative() }).strict();
export const DashboardSummarySchema = z.object({
    period: ReportingPeriodSchema,
    timezone: z.literal('Africa/Cairo'),
    customers: z.object({ total: z.number().int().nonnegative(), createdInPeriod: z.number().int().nonnegative() }).strict(),
    leads: z.object({
        createdInPeriod: z.number().int().nonnegative(),
        nonTerminal: z.number().int().nonnegative(),
        unassignedNonTerminal: z.number().int().nonnegative(),
        byStatus: z.array(DashboardStatusCountSchema),
        byBranch: z.array(DashboardDimensionCountSchema),
        bySource: z.array(DashboardDimensionCountSchema),
    }).strict(),
    tasks: z.object({
        open: z.number().int().nonnegative(), overdue: z.number().int().nonnegative(), completedInPeriod: z.number().int().nonnegative(), workload: z.array(DashboardWorkloadSchema),
    }).strict(),
}).strict();
export const DashboardAttentionItemSchema = z.object({
    itemType: z.enum(['OVERDUE_TASK', 'UNASSIGNED_LEAD']),
    recordId: z.string().uuid(),
    branchId: z.string().uuid(),
    occurredAt: z.string(),
    dueAt: z.string().nullable(),
    assigneeMembershipId: z.string().uuid().nullable(),
    title: z.string().min(1).max(240),
}).strict();
export const DashboardActivityItemSchema = z.object({
    id: z.string().uuid(),
    customerId: z.string().uuid(),
    branchId: z.string().uuid(),
    eventType: z.string().min(1),
    occurredAt: z.string(),
}).strict();
export const DashboardSummaryResponseSchema = z.object({ success: z.literal(true), data: DashboardSummarySchema }).strict();
export const DashboardAttentionResponseSchema = z.object({ success: z.literal(true), data: z.array(DashboardAttentionItemSchema).max(50) }).strict();
export const DashboardActivityResponseSchema = z.object({ success: z.literal(true), data: z.array(DashboardActivityItemSchema).max(50) }).strict();
export const NorthstarTrialIdentity = {
    organizationKey: 'northstar-pharmacy-group',
    datasetKey: 'northstar-client-trial-v1',
    seedNamespace: 'raptor.client-trial.northstar.v1',
    branches: ['northstar-central', 'northstar-riverside'],
    personas: ['owner', 'tenant-admin', 'central-manager', 'central-member', 'riverside-manager', 'riverside-member'],
};
//# sourceMappingURL=client-trial.js.map